import Badge from "@/common/components/Badge";
import Button from "@/common/components/Button";
import { CurrencyTextField } from "@/common/components/CurrencyTextField";
import Modal from "@/common/components/Modal";
import TextArea from "@/common/components/TextArea";
import { FileBoxIcon, FileEditIcon } from "lucide-react";
import { useFormContext } from "react-hook-form";
import { NegotiationCounterValidationSchema } from "../../schemas/negotiationCounter.schema";
import { useEffect, useState } from "react";
import { NegotiationProductData } from "../../types/negotiations.type";
import { formatCurrency } from "@/common/utils/formatters";

interface CounterNegotiationModalProps {
  index: number;
  negotiationProduct: NegotiationProductData;
}

const CounterProductModal = ({ index, negotiationProduct }: CounterNegotiationModalProps) => {
  const [isCounterModalOpen, setIsCounterModalOpen] = useState(false);

  const [initialObservation, setInitialObservation] = useState<string>();
  const [initialValue, setInitialValue] = useState<string>();

  const { register, setValue, watch, trigger, formState: { errors } } = useFormContext<NegotiationCounterValidationSchema>();

  const watchDesiredAction = watch(`negotiation_products_attributes.${index}.desired_action`);

  const onCloseModal = () => {
    setValue(`negotiation_products_attributes.${index}.observation`, initialObservation || '');
    setValue(`negotiation_products_attributes.${index}.value`, initialValue || '');

    setIsCounterModalOpen(false);
  };

  const onConfirm = async () => {
    const isValid = await trigger([
      `negotiation_products_attributes.${index}.observation`,
      `negotiation_products_attributes.${index}.value`
    ]);

    if (isValid) {
      setValue(`negotiation_products_attributes.${index}.desired_action`, 'counter');
      setIsCounterModalOpen(false);
    }
  };

  useEffect(() => {
    if (isCounterModalOpen) {
      setInitialObservation(watch(`negotiation_products_attributes.${index}.observation`));
      setInitialValue(watch(`negotiation_products_attributes.${index}.value`));
    }
  }, [isCounterModalOpen]);

  return (
    <>
      <Modal
        isOpen={isCounterModalOpen}
        setIsOpen={(newValue) => {
          if (!newValue) onCloseModal();
          else setIsCounterModalOpen(newValue);
        }}
        Trigger={(
          <Button variant="yellow-black">
            {watchDesiredAction !== 'accept' ? 'Editar' : 'Contraproposta'}

            <FileEditIcon />
          </Button>
        )}
        title={watchDesiredAction !== 'accept' ? 'Editar contraproposta' : 'Contraproposta'}
        titleClass="text-2xl"
        subtitleClassName="mt-1 text-white-400"
        body={(
          <div className="flex flex-col gap-4 text-white-800">
            <Badge
              Icon={FileBoxIcon}
              iconProps={{ size: 18 }}
              text={negotiationProduct.product.display_name}
              className="rounded-sm flex bg-neutral-op-8 w-fit flex-row-reverse overflow-hidden !p-0 gap-0"
              overwriteClassName
              contentClassName="px-3 py-2"
            />

            <TextArea
              key={`counter-observation-${index}`}
              markAsRequired
              label="Justificativa"
              placeholder="Escreva sua justificativa"
              {...register(`negotiation_products_attributes.${index}.observation`)}
              errorMessage={errors.negotiation_products_attributes?.[index]?.observation?.message || undefined}
            />

            <div className="flex flex-col gap-2">
              <CurrencyTextField
                markAsRequired
                label="Valor da contraproposta"
                {...register(`negotiation_products_attributes.${index}.value`)}
                value={watch(`negotiation_products_attributes.${index}.value`)}
                forceValue
                errorMessage={errors.negotiation_products_attributes?.[index]?.value?.message || undefined}
              />

              <div className="flex flex-col gap-1 items-end">
                <p className="italic font-light text-xs text-white-400">Valor na última contraproposta</p>
                <p className="text-xs">{formatCurrency(Number(negotiationProduct.value))}</p>
              </div>
            </div>
          </div>
        )}
        useCancellableFooter
        cancelButtonText="Voltar"
        confirmButtonText="Salvar"
        cancelButtonClassName="bg-white-200"
        confirmButtonClassName="bg-primary-blue"
        className="rounded-lg overflow-hidden"
        onConfirm={onConfirm}
        onCancel={onCloseModal}
      />
    </>
  );
};

export default CounterProductModal;
